import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Layout } from "Layout";

import { AddCustomerOrder } from "components/AddCustomerOrder";
import { CustomFields } from "components/CustomFields/CustomFields";
import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { QuickAddCustomer } from "components/QuickAddCustomer";
import { FrmTextField } from "components/formFields/FrmTextField";
import { Addresses } from "components/formFields/address/sales/Addresses";
import {
  FrmComment,
  FrmDepartment,
  FrmNumber,
  FrmCustomerWithAdd,
  FrmCustomerNotes,
  FrmCustomerMessage,
  FrmChannel,
  FrmAssignedTo,
  FrmPriority,
  FrmLocation,
  FrmShipMethod,
  FrmCreateBill,
  FrmShipStation,
} from "components/formFields/frm";
import { FrmSignature } from "components/formFields/frm/FrmSignature";
import { DatePicker } from "components/html/Input/DatePicker";
import { DateTimePicker } from "components/html/Input/DateTimePicker";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";
import { WarningModal } from "components/utility/WarningModal";

import { i18n } from "services/i18nService";
import {
  updateTotals,
  ZERO_TOTALS,
} from "services/sosInventoryService/fulfillmentTransaction/updateTotals";
import { setPageDirty } from "services/utility/edit";

import { useCustomFieldDefsAsObject } from "hooks/useCustomFieldDefinitions";
import { useCustomers } from "hooks/useCustomers";
import { useErrors } from "hooks/useErrors";
import { useItems } from "hooks/useItems";
import { useLineHandler } from "hooks/useLineHandler";
import { useRelated } from "hooks/useRelated";

import { LineItems } from "views/FulfillmentTransactions/LineItems";
import {
  getTransaction,
  updateTransaction,
} from "views/Shipments/Shipment/controller";

import { openAlert } from "globalState/alertSlice";
import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { theme } from "SosTheme";
import { OBJECT_TYPES, ITEM_QUICKLIST_CASES } from "appConstants";
import { EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.SHIPMENT.fullString;
const CUSTOM_FIELD_OBJECT_TYPES = [
  OBJECT_TYPE,
  OBJECT_TYPES.CUSTOMER.fullString,
];

export function Shipment(props) {
  const { id, onClose, newFromId, newFromObjectType } = props;

  // TRANSACTION STATE
  const [record, setRecord] = useState();
  const [lines, lineHandler] = useLineHandler();
  const [relatedRecords, setRelatedRecords] = useState({});

  const CURRENT_STATE = { record, lines, relatedRecords };

  // UI STATE
  const { errors, setErrors, isInError } = useErrors();
  const [clearLines, setClearLines] = useState(false);
  const customFieldDefs = useCustomFieldDefsAsObject(CUSTOM_FIELD_OBJECT_TYPES);
  const [addNewCustomer, setAddNewCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [customerAdded, setCustomerAdded] = useState(0);
  const [itemAdded, setItemAdded] = useState(0);
  const [totals, setTotals] = useState(ZERO_TOTALS);
  const loadingState = useSelector((state) => state.loading.editModal);

  // UPDATERS FOR CONTROLLER TO UPDATE STATE
  const UPDATERS = useMemo(
    () => ({
      setRecord,
      lineHandler,
      setRelatedRecords,
      setErrors,
    }),
    [lineHandler, setErrors]
  );

  // RELATED RECORD LISTS
  const classes = useRelated("class");
  const customers = useCustomers(customerAdded);
  const { items, itemsCount } = useItems(ITEM_QUICKLIST_CASES.SALES, itemAdded);

  // SETTINGS
  const departmentEnabled = useSelector(
    (state) => state.userCompanySettings.settings.departmentEnabled
  );

  // OTHER SETUP
  const dispatch = useDispatch();

  // LOAD INITIAL TRANSACTION STATE
  useEffect(() => {
    async function _getTransaction() {
      dispatch(editModalLoadingIndicatorOn());
      await getTransaction(
        id,
        newFromId,
        newFromObjectType,
        UPDATERS,
        customFieldDefs[OBJECT_TYPE]
      );
      dispatch(editModalLoadingIndicatorOff());
    }
    if (customFieldDefs) {
      _getTransaction();
    }
  }, [
    dispatch,
    id,
    newFromId,
    newFromObjectType,
    lineHandler,
    UPDATERS,
    customFieldDefs,
  ]);

  // HANDLE ALL USER INPUT
  function handleInputFieldChange(field, newValue) {
    setPageDirty();
    updateTransaction(
      field,
      newValue,
      customFieldDefs[OBJECT_TYPE],
      CURRENT_STATE,
      UPDATERS
    );
  }

  // ANCILLARY UI FUNCTIONS

  // update totals whenever the line items change
  useEffect(() => updateTotals(lines, setTotals), [lines]);

  // show alert if customer is on credit hold
  useEffect(() => {
    if (relatedRecords.customer?.creditHold) {
      dispatch(
        openAlert({ type: "error", message: i18n("customer.CreditHold") })
      );
    }
  }, [relatedRecords.customer, dispatch]);

  // full add customer
  async function handleAddNewCustomer(customer) {
    setPageDirty();
    handleInputFieldChange("customer", customer);
    setCustomerAdded((prev) => prev + 1);
    setAddNewCustomer(false);
    return;
  }

  // clear lines
  function doClearLines() {
    setPageDirty();
    lineHandler({ type: "clear", blankLine: EMPTY_LINE_ITEM[OBJECT_TYPE] });
    setClearLines(false);
  }

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setRecord}
        objectType={OBJECT_TYPE}
        relatedRecords={relatedRecords}
        lines={lines}
        setErrors={setErrors}
        errors={errors}
        text={record?.number}
        handleClose={onClose}
        customerId={record?.customer?.id}
      />
      {loadingState && <Loading />}
      <ObjectEditDialogContent>
        <Layout
          pageTitle={
            id
              ? `${i18n("objectType.shipment.Sentence")} ${
                  record?.number || ""
                }`
              : i18n("objectType.shipment.New")
          }
        >
          <div id="B" style={{ margin: theme.spacing(2) }}>
            <FormErrors errors={errors} setErrors={setErrors} />
            {record ? (
              <>
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <DateTimePicker
                      name="date"
                      value={record.date}
                      label={i18n("frmLabel.DateTime")}
                      onValueChange={handleInputFieldChange}
                      error={isInError("date")}
                      dataTesting="date"
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmNumber
                      recordId={record.id}
                      label={i18n("shipment.labelNumber")}
                      value={record.number}
                      onValueChange={handleInputFieldChange}
                      error={isInError("number")}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmLocation
                      value={record.location}
                      error={isInError("location")}
                      onValueChange={handleInputFieldChange}
                      width="25rem"
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmCustomerWithAdd
                      value={record.customer}
                      onValueChange={handleInputFieldChange}
                      setAddNewCustomer={setAddNewCustomer}
                      setNewCustomerName={setNewCustomerName}
                      error={isInError("customer")}
                      customers={customers}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmCustomerNotes
                      value={record.customerNotes}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                </FrmFieldGrid>
                <VSpace space={1} />
                <Addresses
                  billing={record.billing}
                  shipping={record.shipping}
                  customer={relatedRecords.customer}
                  handleInputFieldChange={handleInputFieldChange}
                  billingLabel={i18n("address.Customer")}
                  shippingLabel={i18n("address.ShipTo")}
                />
                <VSpace space={2} />
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <AddCustomerOrder
                      objectType={OBJECT_TYPE}
                      customer={record.customer}
                      lineHandler={lineHandler}
                      label={i18n("frmLabel.AddOrder")}
                    />
                  </div>
                </FrmFieldGrid>

                <LineItems
                  record={record}
                  relatedRecords={relatedRecords}
                  objectType={OBJECT_TYPE}
                  lines={lines}
                  items={items}
                  totals={totals}
                  itemsCount={itemsCount}
                  addItem={() => setItemAdded((prev) => prev + 1)}
                  classes={classes}
                  lineHandler={lineHandler}
                  setClearLines={setClearLines}
                  lineItemsErrors={errors.line}
                  setErrors={setErrors}
                  quantityLabelCode="ShipQuantity"
                  showClass
                />

                <VSpace space={2} />

                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <DatePicker
                      name="shipBy"
                      value={record.shipBy}
                      label={i18n("frmLabel.ShipBy")}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmShipMethod
                      maxWidth="20rem"
                      onValueChange={handleInputFieldChange}
                      value={record?.shippingMethod}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmTextField
                      money
                      name="shippingAmount"
                      label={i18n("frmLabel.ShippingCost")}
                      value={record.shippingAmount}
                      onValueBlur={handleInputFieldChange}
                      sx={{ maxWidth: "20rem" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmTextField
                      name="trackingNumber"
                      label={i18n("frmLabel.TrackingNumber")}
                      value={record.trackingNumber}
                      onValueBlur={handleInputFieldChange}
                      maxLength={50}
                      sx={{ maxWidth: "20rem" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmShipStation
                      value={record.forceToShipStation}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmAssignedTo
                      maxWidth="20rem"
                      onValueChange={handleInputFieldChange}
                      value={record?.assignedToUser}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmPriority
                      maxWidth="20rem"
                      onValueChange={handleInputFieldChange}
                      value={record?.priority}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmChannel
                      maxWidth="20rem"
                      onValueChange={handleInputFieldChange}
                      value={record?.channel}
                    />
                  </div>
                  {departmentEnabled && (
                    <div style={{ gridColumn: "1 / 2" }}>
                      <FrmDepartment
                        maxWidth="20rem"
                        value={record.department}
                        onValueChange={handleInputFieldChange}
                      />
                    </div>
                  )}
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmTextField
                      name="customerPO"
                      label={i18n("frmLabel.CustomerPO")}
                      value={record?.customerPO}
                      onValueBlur={handleInputFieldChange}
                      sx={{ maxWidth: "20rem" }}
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmCreateBill
                      value={record.createBillForShippingAmount || false}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2", maxWidth: "100%" }}>
                    <FrmCustomerMessage
                      value={record.customerMessage}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmComment
                      value={record.comment}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmSignature
                      objectType={OBJECT_TYPE}
                      id={record.id}
                      hasSignature={record.hasSignature}
                    />
                  </div>
                </FrmFieldGrid>

                <CustomFields
                  customFieldDefinitions={customFieldDefs[OBJECT_TYPE]}
                  customFields={record.customFields}
                  onValueChange={handleInputFieldChange}
                  setErrors={setErrors}
                />
              </>
            ) : (
              !isInError("record") && (
                <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
              )
            )}
            <VSpace space={4} />
          </div>
          {record && (
            <>
              {!!addNewCustomer && (
                <QuickAddCustomer
                  open
                  initialNameValue={newCustomerName}
                  onClose={() => setAddNewCustomer(false)}
                  onAdd={handleAddNewCustomer}
                />
              )}
              <WarningModal
                title={i18n("global.ClearLines")}
                message={i18n("global.clearLinesMessage")}
                open={clearLines}
                onOk={doClearLines}
                onClose={() => setClearLines(false)}
              />
            </>
          )}
        </Layout>
      </ObjectEditDialogContent>
    </>
  );
}
